/**
 * Page heading / meta helpers for SEO and AI citation signals.
 */

const GENERIC_TITLES = new Set(["home", "homepage", "index"]);

const HOME_HEADING_FALLBACK = "Guided & Self-Guided Alpine Hiking Holidays";

/** Brand suffixes that belong in <title>, not in the visible/sr-only H1. */
const BRAND_TITLE_SUFFIX =
  /\s*[|–—-]\s*Mont Blanc Treks\s*$/i;

export function normalizeHeadingText(
  value: string | null | undefined,
): string {
  return String(value ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Strip trailing "| Mont Blanc Treks" (and similar) so H1 does not repeat the
 * document title brand pattern. Leave full strings for useSeoMeta/title.
 */
export function stripBrandTitleSuffix(
  value: string | null | undefined,
): string {
  const normalized = normalizeHeadingText(value);
  if (!normalized) return "";
  const stripped = normalized.replace(BRAND_TITLE_SUFFIX, "").trim();
  return stripped || normalized;
}

export function isGenericPageTitle(
  title: string | null | undefined,
): boolean {
  const normalized = normalizeHeadingText(title).toLowerCase();
  return !normalized || GENERIC_TITLES.has(normalized);
}

type PageLike = {
  title?: string | null;
  heading1?: string | null;
  intendedTemplate?: string | null;
  template?: string | null;
  meta?: {
    heading1?: string | null;
    metaTitle?: string | null;
    description?: string | null;
  } | null;
};

type SiteLike = {
  title?: string | null;
  description?: string | null;
};

function isHomePage(page?: PageLike | null): boolean {
  const template = String(
    page?.intendedTemplate || page?.template || "",
  ).toLowerCase();
  return template === "home" || isGenericPageTitle(page?.title);
}

/**
 * Prefer an explicit H1 / meta title over CMS placeholder titles like "Home".
 * Always collapses double/trailing whitespace (e.g. TMB H1 spacing).
 * Brand suffixes like "| Mont Blanc Treks" are stripped from H1 only.
 */
export function resolvePageHeading(
  page?: PageLike | null,
  site?: SiteLike | null,
): string {
  if (isHomePage(page)) {
    for (const candidate of [
      page?.meta?.heading1,
      page?.heading1,
      page?.meta?.metaTitle,
    ]) {
      const normalized = stripBrandTitleSuffix(candidate);
      if (normalized && !isGenericPageTitle(normalized)) {
        return normalized;
      }
    }
    return HOME_HEADING_FALLBACK;
  }

  const candidates = [
    page?.meta?.heading1,
    page?.heading1,
    page?.meta?.metaTitle,
    isGenericPageTitle(page?.title) ? null : page?.title,
    site?.title,
  ];

  for (const candidate of candidates) {
    const normalized = stripBrandTitleSuffix(candidate);
    if (normalized && !isGenericPageTitle(normalized)) {
      return normalized;
    }
  }

  return stripBrandTitleSuffix(site?.title) || "Mont Blanc Treks";
}

/**
 * True when a meta description looks cut off mid-phrase.
 */
export function isTruncatedMetaDescription(
  description: string | null | undefined,
): boolean {
  const desc = normalizeHeadingText(description);
  if (!desc) return true;
  if (/[,:;–—-]$/.test(desc)) return true;
  if (/\b(and|or|the|of|to|for|with|a|an|in|on|our|your|from|by)$/i.test(desc)) {
    return true;
  }
  return false;
}

/**
 * Prefer a complete page description; otherwise a unique page-aware fallback.
 */
export function resolveMetaDescription(
  page?: PageLike | null,
  site?: SiteLike | null,
): string {
  const pageDescription = normalizeHeadingText(page?.meta?.description);
  if (pageDescription && !isTruncatedMetaDescription(pageDescription)) {
    return pageDescription;
  }

  const siteDescription = normalizeHeadingText(site?.description);
  const heading = resolvePageHeading(page, site);

  if (isHomePage(page)) {
    if (siteDescription && !isTruncatedMetaDescription(siteDescription)) {
      return siteDescription;
    }
    if (pageDescription) return pageDescription;
    return "Mont Blanc Treks offers guided and self-guided Alpine hiking holidays from Chamonix, specialising in the Tour du Mont Blanc and classic mountain routes.";
  }

  // Unique per page when CMS text is missing or truncated
  if (heading && !isGenericPageTitle(heading)) {
    return `${heading} with Mont Blanc Treks: guided and self-guided Alpine trekking holidays from Chamonix.`;
  }

  if (siteDescription && !isTruncatedMetaDescription(siteDescription)) {
    return siteDescription;
  }

  return (
    pageDescription ||
    "Mont Blanc Treks offers guided and self-guided Alpine trekking holidays from Chamonix."
  );
}
